import { type TrackedEvent, trackEvent } from 'utils/events/trackEvent'

// Add any extra meta data here
export type RetailerCollectionsEventMetadata = { [key: string]: string | number }

enum RetailerCollectionEvents {
  collectionFilterBySearchText = 'collectionFilterBySearchText',
  collectionFilterByActive = 'collectionFilterByActive',
  collectionFilterByCustom = 'collectionFilterByCustom',
  collectionFilterByInstacart = 'collectionFilterByInstacart',
  collectionFilterByEffective = 'collectionFilterByEffective',

  collectionCreatePageSubmit = 'collectionCreatePageSubmit',
  collectionCreatePageSuccess = 'collectionCreatePageSuccess',
  collectionCreatePageFailure = 'collectionCreatePageFailure',
  collectionEditPageSubmit = 'collectionEditPageSubmit',
  collectionEditPageSuccess = 'collectionEditPageSuccess',
  collectionEditPageFailure = 'collectionEditPageFailure',
  collectionCreateBulkModalClick = 'collectionCreateBulkModalClick',
  collectionCreateBulkModalSubmit = 'collectionCreateBulkModalSubmit',
  collectionCreateBulkModalSuccess = 'collectionCreateBulkModalSuccess',
  collectionCreateBulkModalFailure = 'collectionCreateBulkModalFailure',
  collectionDeleteSubmit = 'collectionDeleteSubmit',
  collectionDeleteSuccess = 'collectionDeleteSuccess',
  collectionDeleteFailure = 'collectionDeleteFailure',
  collectionSetActiveSubmit = 'collectionSetActiveSubmit',
  collectionSetActiveSuccess = 'collectionSetActiveSuccess',
  collectionSetActiveFailure = 'collectionSetActiveFailure',
  collectionSetActiveAdminSubmit = 'collectionSetActiveAdminSubmit',
  collectionSetActiveAdminSuccess = 'collectionSetActiveAdminSuccess',
  collectionSetActiveAdminFailure = 'collectionSetActiveAdminFailure',
  collectionSetInactiveSubmit = 'collectionSetInactiveSubmit',
  collectionSetInactiveSuccess = 'collectionSetInactiveSuccess',
  collectionSetInactiveFailure = 'collectionSetInactiveFailure',

  bulkUploadStructureCSVUpload = 'bulkUploadStructureCSVUpload',
  bulkUploadStructureClickReview = 'bulkUploadStructureClickReview',
  bulkUploadStructureSubmit = 'bulkUploadStructureSubmit',
  bulkUploadStructureSuccess = 'bulkUploadStructureSuccess',
  bulkUploadStructureFailure = 'bulkUploadStructureFailure',
  bulkUploadProductCSVUpload = 'bulkUploadProductCSVUpload',
  bulkUploadProductClickReview = 'bulkUploadProductClickReview',
  bulkUploadProductSubmit = 'bulkUploadProductSubmit',
  bulkUploadProductSuccess = 'bulkUploadProductSuccess',
  bulkUploadProductFailure = 'bulkUploadProductFailure',

  productAddEnterUpcModalClick = 'productAddEnterUpcModalClick',
  productAddEnterUpcModalSubmit = 'productAddEnterUpcModalSubmit',
  productAddEnterUpcModalSuccess = 'productAddEnterUpcModalSuccess',
  productAddEnterUpcModalFailure = 'productAddEnterUpcModalFailure',
  productAddSearchCatalogModalClick = 'productAddSearchCatalogModalClick',
  productAddSearchCatalogModalSubmit = 'productAddSearchCatalogModalSubmit',
  productAddSearchCatalogModalSuccess = 'productAddSearchCatalogModalSuccess',
  productAddSearchCatalogModalFailure = 'productAddSearchCatalogModalFailure',
  productRemoveSearchCatalogModalSubmit = 'productRemoveSearchCatalogModalSubmit',
  productRemoveSearchCatalogModalSuccess = 'productRemoveSearchCatalogModalSuccess',
  productRemoveSearchCatalogModalFailure = 'productRemoveSearchCatalogModalFailure',
  productRemoveProductListPageSubmit = 'productRemoveProductListPageSubmit',
  productRemoveProductListPageSuccess = 'productRemoveProductListPageSuccess',
  productRemoveProductListPageFailure = 'productRemoveProductListPageFailure',
}

// In order of an event to be track it must be in this list, else it will be ignored
const EVENT_TRACKING: Record<RetailerCollectionEvents, TrackedEvent> = {
  collectionFilterBySearchText: {
    id: 'retailer_collections.collection.filter.search_text',
    description: 'User filtered collections by the search text field',
  },
  collectionFilterByActive: {
    id: 'retailer_collections.collection.filter.active',
    description: 'User filtered collections by the active filter',
  },
  collectionFilterByCustom: {
    id: 'retailer_collections.collection.filter.custom',
    description: 'User filtered collections by the custom filter',
  },
  collectionFilterByInstacart: {
    id: 'retailer_collections.collection.filter.instacart',
    description: 'User filtered collections by the instacart filter',
  },
  collectionFilterByEffective: {
    id: 'retailer_collections.collection.filter.effective',
    description: 'User filtered collections by the effective filter',
  },

  collectionCreatePageSubmit: {
    id: 'retailer_collections.collection.create.submit',
    description: 'The button to submit collection create is clicked',
  },
  collectionCreatePageSuccess: {
    id: 'retailer_collections.collection.create.success',
    description: 'The request submitted to collection create was successful',
  },
  collectionCreatePageFailure: {
    id: 'retailer_collections.collection.create.failure',
    description: 'The request submitted to collection create failed',
  },
  collectionEditPageSubmit: {
    id: 'retailer_collections.collection.edit.submit',
    description: 'The button to submit collection edit is clicked',
  },
  collectionEditPageSuccess: {
    id: 'retailer_collections.collection.edit.success',
    description: 'The request submitted to collection edit was successful',
  },
  collectionEditPageFailure: {
    id: 'retailer_collections.collection.edit.failure',
    description: 'The request submitted to collection edit failed',
  },
  collectionCreateBulkModalClick: {
    id: 'retailer_collections.collection.create-bulk.click',
    description: 'The button to open collection create bulk is clicked',
  },
  collectionCreateBulkModalSubmit: {
    id: 'retailer_collections.collection.create-bulk.submit',
    description: 'The button to submit collection create bulk is clicked',
  },
  collectionCreateBulkModalSuccess: {
    id: 'retailer_collections.collection.create-bulk.success',
    description: 'The request submitted to collection create bulk was successful',
  },
  collectionCreateBulkModalFailure: {
    id: 'retailer_collections.collection.create-bulk.failure',
    description: 'The request submitted to collection create bulk failed',
  },
  collectionDeleteSubmit: {
    id: 'retailer_collections.collection.delete.submit',
    description: 'The button to submit collection delete is clicked',
  },
  collectionDeleteSuccess: {
    id: 'retailer_collections.collection.delete.success',
    description: 'The request submitted to collection delete was successful',
  },
  collectionDeleteFailure: {
    id: 'retailer_collections.collection.delete.failure',
    description: 'The request submitted to collection delete failed',
  },
  collectionSetActiveSubmit: {
    id: 'retailer_collections.collection.set-active.submit',
    description: 'The button to submit set collection active is clicked',
  },
  collectionSetActiveSuccess: {
    id: 'retailer_collections.collection.set-active.success',
    description: 'The request submitted to set collection active was successful',
  },
  collectionSetActiveFailure: {
    id: 'retailer_collections.collection.set-active.failure',
    description: 'The request submitted to set collection active failed',
  },
  collectionSetActiveAdminSubmit: {
    id: 'retailer_collections.collection.set-active-admin.submit',
    description: 'The button to submit set collection active admin is clicked',
  },
  collectionSetActiveAdminSuccess: {
    id: 'retailer_collections.collection.set-active-admin.success',
    description: 'The request submitted to set collection active admin was successful',
  },
  collectionSetActiveAdminFailure: {
    id: 'retailer_collections.collection.set-active-admin.failure',
    description: 'The request submitted to set collection active admin failed',
  },
  collectionSetInactiveSubmit: {
    id: 'retailer_collections.collection.set-inactive.submit',
    description: 'The button to submit set collection inactive is clicked',
  },
  collectionSetInactiveSuccess: {
    id: 'retailer_collections.collection.set-inactive.success',
    description: 'The request submitted to set collection inactive was successful',
  },
  collectionSetInactiveFailure: {
    id: 'retailer_collections.collection.set-inactive.failure',
    description: 'The request submitted to set collection inactive failed',
  },

  bulkUploadStructureCSVUpload: {
    id: 'retailer_collections.bulk-upload-structure.csv-upload',
    description: 'The process to upload a structure CSV for review (drag and drop CSV)',
  },
  bulkUploadStructureClickReview: {
    id: 'retailer_collections.bulk-upload-structure.review',
    description: 'The button to review bulk upload structure is clicked',
  },
  bulkUploadStructureSubmit: {
    id: 'retailer_collections.bulk-upload-structure.submit',
    description: 'The button to submit bulk upload structure is clicked',
  },
  bulkUploadStructureSuccess: {
    id: 'retailer_collections.bulk-upload-structure.success',
    description: 'The request submitted to bulk upload structure was successful',
  },
  bulkUploadStructureFailure: {
    id: 'retailer_collections.bulk-upload-structure.failure',
    description: 'The request submitted to bulk upload structure failed',
  },
  bulkUploadProductCSVUpload: {
    id: 'retailer_collections.bulk-upload-product.csv-upload',
    description: 'The process to upload a product CSV for review (drag and drop CSV)',
  },
  bulkUploadProductClickReview: {
    id: 'retailer_collections.bulk-upload-product.review',
    description: 'The button to review bulk upload product is clicked',
  },
  bulkUploadProductSubmit: {
    id: 'retailer_collections.bulk-upload-product.submit',
    description: 'The button to submit bulk upload product is clicked',
  },
  bulkUploadProductSuccess: {
    id: 'retailer_collections.bulk-upload-product.success',
    description: 'The request submitted to bulk upload product was successful',
  },
  bulkUploadProductFailure: {
    id: 'retailer_collections.bulk-upload-product.failure',
    description: 'The request submitted to bulk upload product failed',
  },

  productAddEnterUpcModalClick: {
    id: 'retailer_collections.products.add.upc-codes.click',
    description: 'The button to open Enter UPC Codes to add products is clicked',
  },
  productAddEnterUpcModalSubmit: {
    id: 'retailer_collections.products.add.upc-codes.submit',
    description: 'The button to submit add products from Enter UPC Codes is clicked',
  },
  productAddEnterUpcModalSuccess: {
    id: 'retailer_collections.products.add.upc-codes.success',
    description: 'The request submitted to add products from Enter UPC Codes was successful',
  },
  productAddEnterUpcModalFailure: {
    id: 'retailer_collections.products.add.upc-codes.failure',
    description: 'The request submitted to add product from Enter UPC Codes failed',
  },
  productAddSearchCatalogModalClick: {
    id: 'retailer_collections.products.add.search-catalog.click',
    description: 'The button to open search catalog to add products is clicked',
  },
  productAddSearchCatalogModalSubmit: {
    id: 'retailer_collections.products.add.search-catalog.submit',
    description: 'The button to submit add product from search catalog is clicked',
  },
  productAddSearchCatalogModalSuccess: {
    id: 'retailer_collections.products.add.search-catalog.success',
    description: 'The request submitted to add product from search catalog was successful',
  },
  productAddSearchCatalogModalFailure: {
    id: 'retailer_collections.products.add.search-catalog.failure',
    description: 'The request submitted to add product from search catalog failed',
  },
  productRemoveSearchCatalogModalSubmit: {
    id: 'retailer_collections.products.remove.search-catalog.submit',
    description: 'The button to submit remove product from search catalog is clicked',
  },
  productRemoveSearchCatalogModalSuccess: {
    id: 'retailer_collections.products.remove.search-catalog.success',
    description: 'The request submitted to remove product from search catalog was successful',
  },
  productRemoveSearchCatalogModalFailure: {
    id: 'retailer_collections.products.remove.search-catalog.failure',
    description: 'The request submitted to remove product from search catalog failed',
  },
  productRemoveProductListPageSubmit: {
    id: 'retailer_collections.products.remove.list-page.submit',
    description: 'The button to submit remove product from the product list page is clicked',
  },
  productRemoveProductListPageSuccess: {
    id: 'retailer_collections.products.remove.list-page.success',
    description:
      'The request submitted to remove product from the product list page was successful',
  },
  productRemoveProductListPageFailure: {
    id: 'retailer_collections.products.remove.list-page.failure',
    description: 'The request submitted to remove product from the product list page failed',
  },
}

/**
 * Tracks an event without the use of any hooks. Useful for when wanting to track
 * an event while NOT rendering a React component.
 */
export const trackRetailerCollectionsEvent = (
  event: string,
  metadata: RetailerCollectionsEventMetadata
) => {
  const trackEventObject: TrackedEvent = EVENT_TRACKING[event]

  if (trackEventObject) {
    trackEvent({
      ...trackEventObject,
      data: { ...metadata },
    })
  }
}
